import { useEffect, useRef, useState } from 'react';
import { type ChatWidgetPosition, snapToPosition } from './use-chat-widget-position';

// Movement (px) past the origin before a gesture counts as a drag, not a click.
const DRAG_THRESHOLD = 4;
// Anchored-corner inset (px) — matches the button's --percy-gap (1.5rem).
const ANCHOR_GAP = 24;
// Glide duration when a release settles onto its snapped zone.
const SETTLE_MS = 200;

// Resting center for a zone, given the button's half-size.
const zoneCenter = (position: ChatWidgetPosition, halfW: number, halfH: number) => {
  const [row, col] = position.split('-');
  const x = col === 'left' ? ANCHOR_GAP + halfW : col === 'right' ? window.innerWidth - ANCHOR_GAP - halfW : window.innerWidth / 2;
  const y = row === 'top' ? ANCHOR_GAP + halfH : window.innerHeight - ANCHOR_GAP - halfH;
  return {
    x,
    y
  };
};
type UseDraggablePositionArgs = {
  enabled: boolean;
  onPositionChange?: (position: ChatWidgetPosition) => void;
  /** Which zone a release lands in. Defaults to the six-zone grid; corner-only callers narrow it. */
  snap?: (x: number, y: number) => ChatWidgetPosition;
  /**
   * Element to measure for the drag geometry. Defaults to the one carrying the handlers — pass it
   * when the grab handle is a child (a title bar) rather than the element that moves.
   */
  measureRef?: React.RefObject<HTMLElement | null>;
};
type DragHandlers = {
  onPointerDown?: (e: React.PointerEvent<HTMLElement>) => void;
  onClickCapture?: (e: React.MouseEvent<HTMLElement>) => void;
};

/**
 * Makes an element draggable: it follows the cursor while dragging, then snaps to the
 * nearest zone on release and reports it via `onPositionChange`. Tells a click (opens
 * chat) from a drag (suppresses it). Opt-in via `enabled`.
 *
 * Move/up listeners bind to `window` for the gesture (not the element), so a release
 * always lands even if the button re-renders or slides out from under the cursor —
 * the element's own pointerup can be missed, stranding the drag.
 */
const useDraggablePosition = ({
  enabled,
  onPositionChange,
  snap = snapToPosition,
  measureRef
}: UseDraggablePositionArgs) => {
  // Pixel position while dragging/settling; null when idle (class-anchored).
  // `settling` ⇒ a transition glides it to the snapped zone.
  const [drag, setDrag] = useState<{
    x: number;
    y: number;
    settling: boolean;
  } | null>(null);
  // Pointerdown origin + whether the gesture crossed the drag threshold.
  const startRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const movedRef = useRef(false);
  // Detaches the active gesture's window listeners; null when idle.
  const teardownRef = useRef<(() => void) | null>(null);
  // Pending settle timer, so a fresh grab mid-settle cancels it.
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  onPositionChangeRef.current = onPositionChange;

  // Detach any live gesture listeners + timers on unmount.
  useEffect(() => () => {
    teardownRef.current?.();
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
  }, []);
  if (!enabled) {
    return {
      isDragging: false,
      dragStyle: undefined as React.CSSProperties | undefined,
      handlers: {} as DragHandlers
    };
  }
  const endGesture = () => {
    teardownRef.current?.();
    teardownRef.current = null;
    startRef.current = null;
  };
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    // Left button only — a context-menu click mustn't strand a gesture.
    if (e.button !== 0) return;
    endGesture();
    // Grabbing mid-settle cancels the glide so it sticks to the cursor again.
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    startRef.current = {
      x: e.clientX,
      y: e.clientY
    };
    movedRef.current = false;
    const rect = (measureRef?.current ?? e.currentTarget).getBoundingClientRect();
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    // Offset from the cursor to the button's center at grab time — kept constant so the
    // button doesn't jump to re-center under the cursor on the first move.
    const grabDx = e.clientX - (rect.left + halfW);
    const grabDy = e.clientY - (rect.top + halfH);
    const onMove = (ev: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      if (!movedRef.current && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) <= DRAG_THRESHOLD) return;
      movedRef.current = true;
      // Center tracks the cursor minus the grab offset; clamp so the whole pill stays on screen.
      const margin = 8;
      const x = Math.min(Math.max(ev.clientX - grabDx, halfW + margin), window.innerWidth - halfW - margin);
      const y = Math.min(Math.max(ev.clientY - grabDy, halfH + margin), window.innerHeight - halfH - margin);
      setDrag({
        x,
        y,
        settling: false
      });
    };
    const onUp = (ev_0: PointerEvent) => {
      const moved = movedRef.current;
      endGesture();
      if (moved) {
        const snapped = snap(ev_0.clientX, ev_0.clientY);
        // Glide to the zone's resting center, then hand off to the class anchor (same spot).
        const target = zoneCenter(snapped, halfW, halfH);
        setDrag({
          x: target.x,
          y: target.y,
          settling: true
        });
        // movedRef stays true until onClickCapture swallows the synthetic click.
        onPositionChangeRef.current?.(snapped);
        settleTimerRef.current = setTimeout(() => {
          settleTimerRef.current = null;
          setDrag(null);
        }, SETTLE_MS);
      } else {
        setDrag(null); // plain click — let the chat open
      }
    };
    const onCancel = () => {
      endGesture();
      movedRef.current = false;
      setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    teardownRef.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  };

  // A drag-release also fires a click — swallow it so the chat doesn't open.
  const onClickCapture = (e_0: React.MouseEvent<HTMLElement>) => {
    if (!movedRef.current) return;
    e_0.preventDefault();
    e_0.stopPropagation();
    movedRef.current = false;
  };
  const dragStyle: React.CSSProperties | undefined = drag ? {
    position: 'fixed',
    left: drag.x,
    top: drag.y,
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    // 1:1 while tracking the cursor; glide while settling.
    transition: drag.settling ? `left ${SETTLE_MS}ms ease-out, top ${SETTLE_MS}ms ease-out` : 'none'
  } : undefined;
  return {
    // Settling animates on its own, so the grab cursor applies only to a live drag.
    isDragging: drag != null && !drag.settling,
    dragStyle,
    handlers: {
      onPointerDown,
      onClickCapture
    } satisfies DragHandlers
  };
};
export default useDraggablePosition;